import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { StyleSheet, View, TextInput, Pressable, ScrollView, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Image } from 'expo-image';
import { FlashList } from '@shopify/flash-list';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import { useTheme } from '@/context/ThemeContext';
import { useModelStore, useAuthStore, useModalStore, useCompanyStore } from '@/store';
import { useTranslation } from '@/hooks/useTranslation';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import Switch from '@/components/Inputs/Switch';
import PropTypes from 'prop-types';
import { PLUS_COLORS } from '@/constants/Colors';
import Pills from '@/components/Pills';
import Button from '@/components/Buttons/Button';
import useResponsiveness from '@/hooks/useResponsiveness';
import ModelSelectorDetailedDesktop from '@/components/Navigation/ModelSelectorDetailedDesktop';
import { canAccessPlusOnlyModel, isModelLockedForUser, openModelAccessPaywall } from '@/constants/utils/trial';


const createStyles = (colors, isXS, insets = { bottom: 0 }, isDoneButtonShowing = false) => StyleSheet.create({
  container: {
    position: 'relative',
    borderRadius: isXS ? 0 : 18,
    flex: 1,
    flexBasis: 0, // Required for flex scrolling
    minHeight: 0, // Required for flex scrolling
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden', // Required to bound scroll area
  },
  dropdownContentInner: {
    flex: 1,
    flexBasis: 0, // Required for flex scrolling
    minHeight: 0, // Required for flex scrolling
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden', // Required to bound scroll area
  },
  searchContainer: {
    position: 'relative',
    width: '100%',
    marginTop: isXS ? 15 : 10,
    paddingHorizontal: isXS ? 15 : 10,
    flexShrink: 0,
  },
  searchInput: {
    backgroundColor: colors.solid_color_tertiary,
    padding: 8,
    paddingLeft: 32,
    paddingRight: 32,
    borderRadius: 6,
    fontSize: 14,
    height: isXS ? 36 : 'auto',
    color: colors.text_color_primary,
  },
  searchIconContainer: {
    position: 'absolute',
    left: 20,
    top: '50%',
    paddingLeft: isXS ? 5 : 0,
    transform: [{ translateY: -7 }],
    zIndex: 1,
  },
  clearIconContainer: {
    position: 'absolute',
    right: 20,
    top: '50%',
    transform: [{ translateY: -2 }],
    zIndex: 1,
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
  scrollableContainer: {
    flex: 1,
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0, // Required for flex scrolling - forces container to shrink
    minHeight: 0, // Required for flex scrolling on native
  },
  scrollableContainerWeb: {
    overflowY: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
  },
  scrollableContainerContent: {
    paddingTop: 5,
    paddingBottom: 20,
    paddingHorizontal: 5,
  },
  modelItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: isXS ? 15 : 8,
    borderRadius: 8,
    width: '100%',
    gap: 8,
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
  modelItemDisabled: {
    opacity: 0.5,
    // cursor: 'not-allowed', // React Native Web might not support this directly in styles if not using specific pressable props, but styles often work.
  },
  modelItemHovered: {
    backgroundColor: colors.solid_color_tertiary,
  },
  modelItemHoveredSelected: {
    opacity: 0.8,
  },
  modelItemSelected: {
    backgroundColor: colors.solid_color_tertiary,
  },
  modelInfo: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
  },
  modelNameContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flex: 1,
    flexShrink: 1,
    maxWidth: '100%',
  },
  modelNameContainerInner: {
    flexDirection: 'column',
    flex: 1,
    flexShrink: 1,
    maxWidth: '100%',
  },
  newBadge: {
    backgroundColor: '#FF3B30',
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 4,
  },
  addButton: {
    padding: 6,
    borderRadius: 6,
    backgroundColor: colors.solid_color_tertiary,
  },
  triggerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 34,
    paddingLeft: 10,
    paddingRight: 6,
    borderRadius: 18,
  },
  triggerButtonOpen: {
    backgroundColor: colors.solid_color_tertiary,
  },
  triggerButtonHovered: {
    backgroundColor: colors.solid_color_tertiary,
  },
  singleModelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  modelLogoDropdown: {
    width: 32,
    height: 32,
    backgroundColor: '#fff',
    borderRadius: 0,
  },
  modelLogoTrigger: {
    width: 16,
    height: 16,
    borderRadius: 0,
    backgroundColor: '#fff',
  },
  chevronIcon: {
    marginLeft: 4,
  },
  loadingContainer: {
    padding: 10,
    alignItems: 'center',
  },
  errorContainer: {
    padding: 10,
    alignItems: 'center',
  },
  noResultsContainer: {
    padding: 10,
    alignItems: 'center',
  },
  multiModelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  sectionHeader: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    top: 0,
    zIndex: 2,
    backgroundColor: colors.solid_color_tertiary,
  },
  modelNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  modelDescription: {
    marginTop: 2,
    flexWrap: 'wrap',
    flexShrink: 1,
  },
  selectionIndicator: {
    width: 14,
    height: 14,
    borderRadius: 7,
    borderWidth: 1.5,
    borderColor: colors.text_color_tertiary,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  selectionIndicatorSelected: {
    borderColor: colors.text_color_primary,
    backgroundColor: 'transparent',
  },
  selectionDot: {
    width: 7,
    height: 7,
    borderRadius: 3.5,
    backgroundColor: colors.text_color_primary,
  },
  modelLogo: {
    width: 28,
    height: 28,
    borderRadius: 4,
    backgroundColor: '#fff',
    flexShrink: 0,
  },
  modelLogoPlaceholder: {
    width: 28,
    height: 28,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.solid_color_tertiary,
    flexShrink: 0,
  },
  creditCostBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    marginRight: 6,
    flexShrink: 0,
  },
  plusOnlyContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    ...(Platform.OS === 'web' && { backgroundImage: PLUS_COLORS.backgroundImage }),
    height: 18,
    paddingHorizontal: 6,
    paddingVertical: 1,
    borderRadius: 4,
  },
  plusOnlyText: {
    textShadow: PLUS_COLORS.textShadow,
    color: PLUS_COLORS.textColor,
  },
  plusOnlyIcon: {
    marginRight: 1,
    color: PLUS_COLORS.iconColor,
  },
  multiSelectContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 10,
    paddingRight: 8,
    paddingTop: 8,
    paddingBottom: 0,
    flexShrink: 0,
    gap: 6,
  },
  multiSelectDescription: {
    marginLeft: 4,
    marginRight: 'auto',
  },
  // Keep old names as aliases for backwards compatibility
  autoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: isXS ? 15 : 10,
    paddingRight: isXS ? 15 : 8,
    paddingTop: 8,
    paddingBottom: 0,
    flexShrink: 0,
    gap: 6,
  },
  autoContainerDescription: {
    marginLeft: 4,
    marginRight: 'auto',
  },
  pillsOuterContainer: {
    marginTop: 6,
    paddingTop: 4,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: colors.border_color_secondary,
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
    backgroundColor: 'transparent',
    flexShrink: 0,
  },
  pillsContainer: {
    marginLeft: 4,
    marginRight: 4,
  },
  doneButtonContainer: {
    padding: isXS ? 15 : 8,
    paddingBottom: isXS ? (60 + (insets.bottom ?? 0)) : 10,
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
    flexShrink: 0,
  },
  selectedModelsContainer: {
    padding: isXS ? 15 : 8,
    paddingBottom: isXS && !isDoneButtonShowing ? (8 + (insets.bottom ?? 0)) : 10,
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_primary,
    flexShrink: 0,
  },
  selectedModelsTitle: {
    marginBottom: isXS ? 12 : 8,
    paddingHorizontal: 4,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  selectedModelsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    paddingBottom: isXS ? 10 : 0,
    paddingHorizontal: isXS ? 4 : 0,
  },
  selectedModelChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.solid_color_tertiary,
    borderRadius: 8,
    paddingVertical: 5,
    paddingHorizontal: 10,
    gap: 8,
    height: 26,
  },
});

// Lightweight stylesheet for list rows — avoids rebuilding the full modal
// StyleSheet on every row render.
const createModelItemStyles = (colors, isXS) => StyleSheet.create({
  modelItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: isXS ? 15 : 8,
    borderRadius: 8,
    width: '100%',
    gap: 8,
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
  modelItemDisabled: {
    opacity: 0.5,
  },
  modelItemHovered: {
    backgroundColor: colors.solid_color_tertiary,
  },
  modelItemHoveredSelected: {
    opacity: 0.8,
  },
  modelItemSelected: {
    backgroundColor: colors.solid_color_tertiary,
  },
  modelInfo: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
  },
  modelNameContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flex: 1,
    flexShrink: 1,
    maxWidth: '100%',
  },
  modelNameContainerInner: {
    flexDirection: 'column',
    flex: 1,
    flexShrink: 1,
    maxWidth: '100%',
  },
  modelNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  modelDescription: {
    marginTop: 2,
    flexWrap: 'wrap',
    flexShrink: 1,
  },
  infoButton: {
    padding: 4,
    marginLeft: 2,
    flexShrink: 0,
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
  newBadge: {
    backgroundColor: '#FF3B30',
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 4,
  },
  selectionIndicator: {
    width: 14,
    height: 14,
    borderRadius: 7,
    borderWidth: 1.5,
    borderColor: colors.text_color_tertiary,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  selectionIndicatorSelected: {
    borderColor: colors.text_color_primary,
    backgroundColor: 'transparent',
  },
  selectionDot: {
    width: 7,
    height: 7,
    borderRadius: 3.5,
    backgroundColor: colors.text_color_primary,
  },
  modelLogo: {
    width: 28,
    height: 28,
    borderRadius: 4,
    backgroundColor: '#fff',
    flexShrink: 0,
  },
  modelLogoPlaceholder: {
    width: 28,
    height: 28,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.solid_color_tertiary,
    flexShrink: 0,
  },
  creditCostBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    marginRight: 6,
    flexShrink: 0,
  },
  plusOnlyContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    ...(Platform.OS === 'web' && { backgroundImage: PLUS_COLORS.backgroundImage }),
    height: 18,
    paddingHorizontal: 6,
    paddingVertical: 1,
    borderRadius: 4,
  },
  plusOnlyText: {
    textShadow: PLUS_COLORS.textShadow,
    color: PLUS_COLORS.textColor,
  },
  plusOnlyIcon: {
    marginRight: 1,
    color: PLUS_COLORS.iconColor,
  },
});

const ModelItem = React.memo(function ModelItem({
  model,
  onActionClick,
  onInfoClick,
  isSelected,
  isDisabled,
  userIsPlus,
  colors,
  isXS,
}) {
  const { t } = useTranslation();
  const styles = React.useMemo(() => createModelItemStyles(colors, isXS), [colors, isXS]);
  const [isHovered, setIsHovered] = useState(false);
  const isWeb = Platform.OS === 'web';

  const isNew = useMemo(() => {
    if (!model.createdAt) return false;
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return new Date(model.createdAt) > sevenDaysAgo;
  }, [model.createdAt]);

  const isPlusOnly = model.flags?.isPlusOnly;
  const isVideoModel = model.capabilities?.output?.hasVideo;
  const isImageOnly = !!model.capabilities?.output?.hasImages && !isVideoModel;
  // Free users can use image-only models; video/other gated need Plus or paid packs
  const isActuallyPlusOnly = !userIsPlus && !isImageOnly && !!(isPlusOnly || isVideoModel);

  const handlePress = useCallback(() => {
    if (!isDisabled) onActionClick(model);
  }, [isDisabled, onActionClick, model]);

  const handleInfoPress = useCallback((e) => {
    // Nested pressable — keep the row's select handler from firing.
    e?.stopPropagation?.();
    onInfoClick?.(model);
  }, [onInfoClick, model]);

  return (
    <Pressable 
      style={[
        styles.modelItem,
        isWeb && isHovered && !isSelected && !isDisabled && styles.modelItemHovered,
        isWeb && isHovered && isSelected && !isDisabled && styles.modelItemHoveredSelected,
        isSelected && styles.modelItemSelected,
        isDisabled && styles.modelItemDisabled
      ]}
      onMouseEnter={isWeb ? () => setIsHovered(true) : undefined}
      onMouseLeave={isWeb ? () => setIsHovered(false) : undefined}
      onPress={handlePress}
    >
      <View style={[
        styles.selectionIndicator,
        isSelected && styles.selectionIndicatorSelected,
        isDisabled && { borderColor: colors.border_color_secondary }
      ]}>
        {isSelected && <View style={styles.selectionDot} />}
      </View>
      {model.logoPath ? (
        <Image
          source={{ uri: model.logoPath }}
          style={styles.modelLogo}
          contentFit="cover"
          recyclingKey={model.id}
          cachePolicy="memory-disk"
        />
      ) : (
        <View style={styles.modelLogoPlaceholder}>
          <Icon name="sparkles" size={12} color={colors.text_color_tertiary} />
        </View>
      )}
      <View style={styles.modelInfo}>
        <View style={styles.modelNameContainer}>
          <View style={styles.modelNameContainerInner}>
            <View style={styles.modelNameRow}>
              <Text color={isDisabled ? "tertiary" : "primary"} size="n" numberOfLines={1}>{model.name}</Text>
              {isNew && (
                <View style={styles.newBadge}>
                  <Text color="white" size="xs">{t('nav.modelSelectorDetailed.newBadge')}</Text>
                </View>
              )}
            </View>
          </View>
        </View>
      </View>
      {model.creditCosts?.baseCost != null && model.creditCosts.baseCost > 0 && !model.capabilities?.output?.hasVideo && (
        <View style={styles.creditCostBadge}>
          <Icon name="coin" solid size={9} color={colors.text_color_tertiary} />
          <Text color="tertiary" size="xxs">{model.creditCosts.baseCost}</Text>
        </View>
      )}
      {isActuallyPlusOnly && (
        <View style={styles.plusOnlyContainer}>
          <Icon name="sparkles" solid size={8} color={PLUS_COLORS.iconColor} style={styles.plusOnlyIcon} />
          <Text color={PLUS_COLORS.textColor} size="xxs" weight="extraBold" style={styles.plusOnlyText}>PLUS</Text>
        </View>
      )}
      {onInfoClick && (
        <Pressable
          style={styles.infoButton}
          onPress={handleInfoPress}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel={t('nav.modelSelectorDetailed.moreInfo')}
        >
          <Icon name="circle-info" size={15} color={colors.text_color_tertiary} />
        </Pressable>
      )}
    </Pressable>
  );
});

const AutoSelectItem = React.memo(function AutoSelectItem({ isSelected, onPress, colors, isXS }) {
  const { t } = useTranslation();
  const styles = React.useMemo(() => createModelItemStyles(colors, isXS), [colors, isXS]);
  const [isHovered, setIsHovered] = useState(false);
  const isWeb = Platform.OS === 'web';

  return (
    <Pressable 
      style={[
        styles.modelItem,
        isWeb && isHovered && !isSelected && styles.modelItemHovered,
        isSelected && styles.modelItemSelected,
      ]}
      onMouseEnter={isWeb ? () => setIsHovered(true) : undefined}
      onMouseLeave={isWeb ? () => setIsHovered(false) : undefined}
      onPress={onPress}
    >
      <View style={[
        styles.selectionIndicator,
        isSelected && styles.selectionIndicatorSelected,
      ]}>
        {isSelected && <View style={styles.selectionDot} />}
      </View>
      <View style={styles.modelLogoPlaceholder}>
        <Icon name="wand-magic-sparkles" size={12} color={colors.text_color_tertiary} />
      </View>
      <View style={styles.modelInfo}>
        <View style={styles.modelNameContainer}>
          <View style={styles.modelNameContainerInner}>
            <View style={styles.modelNameRow}>
              <Text color="primary" size="n" numberOfLines={1}>{t('nav.modelSelectorDetailed.autoSelect')}</Text>
            </View>
            <Text
              color="secondary"
              size="xs"
              style={styles.modelDescription}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {t('nav.modelSelectorDetailed.autoSelectDesc')}</Text>
          </View>
        </View>
      </View>
    </Pressable>
  );
});

const createModelInfoStyles = (colors) => StyleSheet.create({
  infoContainer: {
    flex: 1,
    flexBasis: 0,
    minHeight: 0,
  },
  infoHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 15,
    paddingTop: 15,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.border_color_secondary,
    flexShrink: 0,
  },
  infoBackButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 4,
    paddingRight: 8,
    marginLeft: -4,
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
  infoScroll: {
    flex: 1,
    flexBasis: 0,
    minHeight: 0,
  },
  infoScrollContent: {
    paddingHorizontal: 15,
    paddingTop: 15,
    paddingBottom: 20,
  },
  infoModelHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  infoModelLogo: {
    width: 44,
    height: 44,
    borderRadius: 8,
    backgroundColor: '#fff',
    flexShrink: 0,
  },
  infoModelLogoPlaceholder: {
    width: 44,
    height: 44,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.solid_color_tertiary,
    flexShrink: 0,
  },
  infoModelNameContainer: {
    flex: 1,
    flexShrink: 1,
  },
  infoModelNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 1,
  },
  infoDescription: {
    lineHeight: 20,
  },
  infoSection: {
    marginTop: 18,
  },
  infoSectionTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 10,
  },
  infoSectionLine: {
    flex: 1,
    height: 1,
    backgroundColor: colors.border_color_secondary,
  },
  infoChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  infoChip: {
    backgroundColor: colors.solid_color_tertiary,
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  infoStrengthsList: {
    gap: 8,
  },
  infoStrengthItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  infoStrengthIcon: {
    width: 16,
    height: 16,
    borderRadius: 4,
    backgroundColor: colors.solid_color_tertiary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoStatsList: {
    gap: 8,
  },
  infoStatRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  infoStatIcon: {
    width: 16,
    alignItems: 'center',
  },
  infoNewBadge: {
    backgroundColor: '#FF3B30',
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 4,
  },
  infoFooter: {
    padding: 15,
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
    flexShrink: 0,
  },
});

// In-sheet model details view (mobile). The modal system hosts a single
// modal at a time, so opening ModelDetailsModal from here would tear down
// the selector and lose the user's search/selection state — instead the
// info button swaps the list for this panel and Back restores it.
const ModelInfoView = React.memo(function ModelInfoView({ model, isSelected, onBack, onSelect, colors, insets }) {
  const { t } = useTranslation();
  const styles = React.useMemo(() => createModelInfoStyles(colors), [colors]);
  const company = useCompanyStore(state => state.getCompanyById(model.companyId));

  const isNew = useMemo(() => {
    if (!model.createdAt) return false;
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return new Date(model.createdAt) > sevenDaysAgo;
  }, [model.createdAt]);

  const keywords = Array.isArray(model.keywords) ? model.keywords : [];
  const strengths = Array.isArray(model.strengths) ? model.strengths : [];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const statsItems = useMemo(() => [
    ...(model.contextWindow ? [{
      icon: 'puzzle-piece',
      text: t('nav.modelSelectorDetailed.statsContextWindow').replace('{count}', model.contextWindow.toLocaleString()),
    }] : []),
    ...(model.maxOutputTokens ? [{
      icon: 'puzzle-piece',
      text: t('nav.modelSelectorDetailed.statsMaxOutput').replace('{count}', model.maxOutputTokens.toLocaleString()),
    }] : []),
    ...(model.creditCosts?.baseCost != null && model.creditCosts.baseCost > 0 ? [{
      icon: 'coin',
      text: t('nav.modelSelectorDetailed.statsCredits').replace('{count}', String(model.creditCosts.baseCost)),
    }] : []),
    ...(model.releasedDate ? [{
      icon: 'calendar',
      text: t('nav.modelSelectorDetailed.statsReleased').replace('{date}', formatDate(model.releasedDate)),
    }] : []),
  ], [model, t]);

  return (
    <View style={styles.infoContainer}>
      <View style={styles.infoHeader}>
        <Pressable
          style={styles.infoBackButton}
          onPress={onBack}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel={t('nav.modelSelectorDetailed.back')}
        >
          <Icon name="chevron-left" size={13} color={colors.text_color_primary} />
          <Text color="primary" size="n">{t('nav.modelSelectorDetailed.back')}</Text>
        </Pressable>
      </View>
      <ScrollView
        style={styles.infoScroll}
        contentContainerStyle={styles.infoScrollContent}
        showsVerticalScrollIndicator={true}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.infoModelHeader}>
          {model.logoPath ? (
            <Image
              source={{ uri: model.logoPath }}
              style={styles.infoModelLogo}
              contentFit="cover"
              recyclingKey={model.id}
              cachePolicy="memory-disk"
            />
          ) : (
            <View style={styles.infoModelLogoPlaceholder}>
              <Icon name="sparkles" size={18} color={colors.text_color_tertiary} />
            </View>
          )}
          <View style={styles.infoModelNameContainer}>
            <View style={styles.infoModelNameRow}>
              <Text color="primary" size="l" weight="semibold" numberOfLines={2}>{model.name}</Text>
              {isNew && (
                <View style={styles.infoNewBadge}>
                  <Text color="white" size="xs">{t('nav.modelSelectorDetailed.newBadge')}</Text>
                </View>
              )}
            </View>
            {company?.name && (
              <Text color="secondary" size="s" numberOfLines={1}>{company.name}</Text>
            )}
          </View>
        </View>

        {!!model.description && (
          <Text color="secondary" size="n" style={styles.infoDescription}>{model.description}</Text>
        )}

        {keywords.length > 0 && (
          <View style={styles.infoSection}>
            <View style={styles.infoSectionTitleContainer}>
              <Text color="primary" size="xs" weight="semibold">{t('nav.modelSelectorDetailed.bestFor').toUpperCase()}</Text>
              <View style={styles.infoSectionLine} />
            </View>
            <View style={styles.infoChips}>
              {keywords.map((keyword, index) => (
                <View key={`keyword-${index}`} style={styles.infoChip}>
                  <Text color="secondary" size="xs">{keyword.charAt(0).toUpperCase() + keyword.slice(1)}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {strengths.length > 0 && (
          <View style={styles.infoSection}>
            <View style={styles.infoSectionTitleContainer}>
              <Text color="primary" size="xs" weight="semibold">{t('nav.modelSelectorDetailed.strengths').toUpperCase()}</Text>
              <View style={styles.infoSectionLine} />
            </View>
            <View style={styles.infoStrengthsList}>
              {strengths.map((strength, index) => (
                <View key={`strength-${index}`} style={styles.infoStrengthItem}>
                  <View style={styles.infoStrengthIcon}>
                    <Icon name="check" size={9} color={colors.text_color_primary} />
                  </View>
                  <Text color="primary" size="n">{strength.charAt(0).toUpperCase() + strength.slice(1)}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {statsItems.length > 0 && (
          <View style={styles.infoSection}>
            <View style={styles.infoSectionTitleContainer}>
              <Text color="primary" size="xs" weight="semibold">{t('nav.modelSelectorDetailed.stats').toUpperCase()}</Text>
              <View style={styles.infoSectionLine} />
            </View>
            <View style={styles.infoStatsList}>
              {statsItems.map((item, index) => (
                <View key={`stat-${index}`} style={styles.infoStatRow}>
                  <View style={styles.infoStatIcon}>
                    <Icon name={item.icon} size={11} color={colors.text_color_secondary} />
                  </View>
                  <Text color="secondary" size="n">{item.text}</Text>
                </View>
              ))}
            </View>
          </View>
        )}
      </ScrollView>
      <View style={[styles.infoFooter, { paddingBottom: 15 + (insets?.bottom ?? 0) }]}>
        <Button
          text={isSelected ? t('nav.modelSelectorDetailed.selectedModel') : t('nav.modelSelectorDetailed.useThisModel')}
          variant={isSelected ? 'secondary' : 'primary'}
          size="medium"
          prependIcon={isSelected ? 'check' : undefined}
          onPress={onSelect}
          isFullWidth
        />
      </View>
    </View>
  );
});

const createNoResultsStyles = (colors) => StyleSheet.create({
  container: {
    width: '100%',
    flexShrink: 0,
  },
  messageContainer: {
    padding: 10,
    paddingTop: 24,
    paddingBottom: 14,
    alignItems: 'center',
  },
  suggestionsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 15,
    marginBottom: 6,
  },
  suggestionsLine: {
    flex: 1,
    height: 1,
    backgroundColor: colors.border_color_secondary,
  },
});

// Empty-search state: keeps the "no models found" message but also suggests
// the newest catalog additions (as fully interactive rows) so the user has
// somewhere useful to go next.
const SearchNoResults = React.memo(function SearchNoResults({ latestModels, onActionClick, onInfoClick, userIsPlus, colors, isXS }) {
  const { t } = useTranslation();
  const styles = React.useMemo(() => createNoResultsStyles(colors), [colors]);
  return (
    <View style={styles.container}>
      <View style={styles.messageContainer}>
        <Text color="secondary">{t('nav.modelSelectorDetailed.noModels')}</Text>
      </View>
      {latestModels.length > 0 && (
        <>
          <View style={styles.suggestionsHeader}>
            <Text color="tertiary" size="xs" weight="semibold">{t('nav.modelSelectorDetailed.latestModels').toUpperCase()}</Text>
            <View style={styles.suggestionsLine} />
          </View>
          {latestModels.map((model) => (
            <ModelItem
              key={`latest-${model.id}`}
              model={model}
              onActionClick={onActionClick}
              onInfoClick={onInfoClick}
              isSelected={false}
              isDisabled={false}
              userIsPlus={userIsPlus}
              colors={colors}
              isXS={isXS}
            />
          ))}
        </>
      )}
    </View>
  );
});

const ModelSelectorDetailed = ({
  modelIds,
  onChange,
  maxModels = 5,
  inputs,
  outputs,
  tags,
  // Optional explicit capability path to filter by — e.g.
  //   "capabilities.output.has3D"
  // Mirrors `tool.modelFilter.capability` on the backend so callers can
  // pass it through verbatim instead of mapping `tool.outputType` strings
  // through a switch. Takes precedence as an additional gate; if set,
  // a model must have a truthy value at the dotted path.
  capabilityPath,
  // Optional list of allowed model slugs. Mirrors `tool.modelFilter.slugs`.
  // When set, only models whose `slug` is included pass the filter.
  allowedSlugs,
  shouldCloseOnSelect = true,
  isDisabled,
  onRequestClose,
  noAutoMode = false,
  noMultiSelectMode = false,
  showDoneButton = false,
  showSelectedModels = false,
  restrictMultipleModelsToSameType = false,
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedModelIds, setSelectedModelIds] = useState(modelIds || []);
  // Default to single-select. Multi-select should only turn on when the
  // caller explicitly arrives with more than one model already selected,
  // or when the user flips the toggle themselves. Previously this defaulted
  // to `!noMultiSelectMode`, which meant the chat input (which passes
  // `noMultiSelectMode: false`) always opened in multi-select mode.
  const [isMultiSelectEnabled, setIsMultiSelectEnabled] = useState(
    Array.isArray(modelIds) && modelIds.length > 1
  );
  // Native switches can dispatch a model-row press before React commits the
  // toggle's state update. Keep the latest mode available synchronously so
  // that first press uses the multi-model limit and does not close the sheet.
  const isMultiSelectEnabledRef = useRef(
    Array.isArray(modelIds) && modelIds.length > 1
  );
  const [activeTag, setActiveTag] = useState('all');
  // When set, the mobile list swaps to the inline model-info panel. Kept as
  // local state (not a stacked modal) so search/selection survive a Back tap.
  const [detailsModelId, setDetailsModelId] = useState(null);
  const { openModal } = useModalStore(state => state.actions);
  const user = useAuthStore(state => state.user);
  const userIsPlus = !!user?.flags?.isPlus;
  // Paid packs unlock video + non-image gated models; image-only is free-user OK
  const canUseGatedModels = canAccessPlusOnlyModel(user);

  // Effective max models based on multi-select toggle (if noMultiSelectMode, always single select)
  const effectiveMaxModels = noMultiSelectMode ? 1 : (isMultiSelectEnabled ? maxModels : 1);
  const searchInputRef = useRef(null);

  const isTopHidden = useMemo(() => {
    return !searchQuery.trim() && !inputs?.length && !outputs?.length && !capabilityPath && !allowedSlugs?.length;
  }, [searchQuery, inputs, outputs, capabilityPath, allowedSlugs]);
  const { isXS } = useResponsiveness();
  const useVirtualizedList = Platform.OS !== 'web' && isXS;
  const isDoneButtonShowing = useMemo(() => showDoneButton && effectiveMaxModels > 1, [showDoneButton, effectiveMaxModels]);
  const insets = useSafeAreaInsets();
  const styles = React.useMemo(() => createStyles(colors, isXS, insets, isDoneButtonShowing), [colors, isXS, insets, isDoneButtonShowing]);

  const models = useModelStore(state => state.models);
  const allModelIds = useModelStore(state => state.allModelIds);
  const searchResults = useModelStore(state => state.searchResults);
  const isSearching = useModelStore(state => state.isSearching);
  const searchError = useModelStore(state => state.searchError);
  const modelTags = useModelStore(state => state.modelTags);
  const isLoadingModelTags = useModelStore(state => state.isLoadingModelTags);
  const modelTagsError = useModelStore(state => state.modelTagsError);

  const isLoadingAllModels = useModelStore(state => state.isLoadingAllModels);
  const isFetchedAllModels = useModelStore(state => state.isFetchedAllModels);
  const allModelsPagination = useModelStore(state => state.pagination.allModels);
  const clearSearch = useModelStore(state => state.clearSearch);
  const searchModels = useModelStore(state => state.searchModels);
  const fetchAllModels = useModelStore(state => state.fetchAllModels);
  const fetchModelTags = useModelStore(state => state.fetchModelTags);
  const getModelsByTag = useModelStore(state => state.getModelsByTag);
  const fetchModelsByTag = useModelStore(state => state.fetchModelsByTag);
  const frequentModelIds = useModelStore(state => state.frequentModelIds);
  const isFetchedFrequentModels = useModelStore(state => state.isFetchedFrequentModels);
  const isLoadingFrequentModels = useModelStore(state => state.isLoadingFrequentModels);
  const fetchFrequentModels = useModelStore(state => state.fetchFrequentModels);

  const favoriteModelIds = useAuthStore(state => state.user?.favoriteModelIds) || [];

  // Guard against the fetch-all-models auto-loop re-triggering between pages.
  // The store auto-fetches remaining pages internally, so the component should
  // only ever kick off the initial fetch once per mount.
  const hasInitiatedFetchAllRef = useRef(false);

  const selectedModels = useMemo(
    () => selectedModelIds.map(id => models[id]).filter(Boolean),
    [selectedModelIds, models]
  );

  const selectedModelIdsSet = useMemo(
    () => new Set(selectedModelIds),
    [selectedModelIds]
  );

  // Read through the store so the info panel always shows fresh model data.
  const detailsModel = detailsModelId ? models[detailsModelId] : null;

  // Initialize selectedModelIds when modelIds prop is explicitly provided
  // Don't reset when modelIds is undefined - keep current selection
  useEffect(() => {
    if (modelIds !== undefined && modelIds !== null) {
      const ids = Array.isArray(modelIds) ? modelIds : [modelIds];
      setSelectedModelIds(ids);
      // Enable multi-select if more than 1 model is already selected
      if (ids.length > 1) {
        isMultiSelectEnabledRef.current = true;
        setIsMultiSelectEnabled(true);
      }
    }
    // Note: Don't reset to [] when modelIds is undefined - this allows 
    // the selection to persist on Android where modelIds may not be passed
  }, [modelIds]);

  // Fetch all models, frequent models, and model tags on mount if not already loaded.
  // IMPORTANT: the `fetchAllModels` guard uses a ref so it cannot be re-invoked
  // by dep changes mid-flight. The store auto-paginates through every page after
  // the first call; re-triggering from here would reset `allModelIds` to page 1
  // and prevent subsequent pages from ever landing.
  useEffect(() => {
    const runFetch = () => {
      if (!hasInitiatedFetchAllRef.current && !isLoadingAllModels && !isFetchedAllModels) {
        hasInitiatedFetchAllRef.current = true;
        try {
          fetchAllModels();
        } catch (error) {
          hasInitiatedFetchAllRef.current = false;
          console.error('Failed to fetch all models:', error);
        }
      }

      if (!isLoadingFrequentModels && !isFetchedFrequentModels) {
        try {
          fetchFrequentModels();
        } catch (error) {
          console.error('Failed to fetch frequent models:', error);
        }
      }

      if (Object.keys(modelTags).length === 0 && !modelTagsError && !isLoadingModelTags) {
        fetchModelTags();
      }
    };

    runFetch();
  }, [fetchAllModels, isLoadingAllModels, isFetchedAllModels, allModelIds.length, fetchFrequentModels, isLoadingFrequentModels, isFetchedFrequentModels, modelTags, fetchModelTags, isLoadingModelTags, modelTagsError]);

  // Cleanup search when component unmounts
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      clearSearch();
    };
  }, []);

  // Clear search when dropdown is opened
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      clearSearch();
    }
  }, [isOpen]);

  // Focus search input when component mounts (web only — on native iOS/Android
  // auto-focus triggers the keyboard which covers the modal)
  useEffect(() => {
    if (Platform.OS === 'web' && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Handle search with debounce
  const handleSearch = (text) => {
    setSearchQuery(text);
    
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Reset active tag to 'all' when searching
    if (text.trim()) {
      setActiveTag('all');
    }

    // Clear search if query is empty
    if (!text.trim()) {
      clearSearch();
      return;
    }
    
    const timeout = setTimeout(() => {
      if (text.trim()) {
        searchModels(text);
      }
    }, 500);
    
    setSearchTimeout(timeout);
  };

  // Helper function to get required capabilities from selected models
  const getRequiredCapabilitiesFromSelected = () => {
    if (selectedModels.length === 0) return null;

    // Start with the first model's capabilities
    const required = {
      input: {
        hasTextSupport: selectedModels[0].capabilities.input.hasTextSupport,
        hasFileSupport: selectedModels[0].capabilities.input.hasFileSupport,
        hasImageSupport: selectedModels[0].capabilities.input.hasImageSupport,
        hasAudioSupport: selectedModels[0].capabilities.input.hasAudioSupport,
        hasVideoSupport: selectedModels[0].capabilities.input.hasVideoSupport
      },
      output: {
        hasText: selectedModels[0].capabilities.output.hasText,
        hasImages: selectedModels[0].capabilities.output.hasImages,
        hasAudio: selectedModels[0].capabilities.output.hasAudio,
        hasVideo: selectedModels[0].capabilities.output.hasVideo,
        hasStreaming: selectedModels[0].capabilities.output.hasStreaming,
        hasThinking: selectedModels[0].capabilities.output.hasThinking,
        hasCitations: selectedModels[0].capabilities.output.hasCitations,
        hasWebSearch: selectedModels[0].capabilities.output.hasWebSearch,
        hasFunctionCalling: selectedModels[0].capabilities.output.hasFunctionCalling,
        hasToolUse: selectedModels[0].capabilities.output.hasToolUse
      }
    };

    // For each additional selected model, keep only the capabilities that all models share
    for (let i = 1; i < selectedModels.length; i++) {
      const model = selectedModels[i];
      
      // Input capabilities
      required.input.hasTextSupport = required.input.hasTextSupport && model.capabilities.input.hasTextSupport;
      required.input.hasFileSupport = required.input.hasFileSupport && model.capabilities.input.hasFileSupport;
      required.input.hasImageSupport = required.input.hasImageSupport && model.capabilities.input.hasImageSupport;
      required.input.hasAudioSupport = required.input.hasAudioSupport && model.capabilities.input.hasAudioSupport;
      required.input.hasVideoSupport = required.input.hasVideoSupport && model.capabilities.input.hasVideoSupport;

      // Output capabilities
      required.output.hasText = required.output.hasText && model.capabilities.output.hasText;
      required.output.hasImages = required.output.hasImages && model.capabilities.output.hasImages;
      required.output.hasAudio = required.output.hasAudio && model.capabilities.output.hasAudio;
      required.output.hasVideo = required.output.hasVideo && model.capabilities.output.hasVideo;
      required.output.hasStreaming = required.output.hasStreaming && model.capabilities.output.hasStreaming;
      required.output.hasThinking = required.output.hasThinking && model.capabilities.output.hasThinking;
      required.output.hasCitations = required.output.hasCitations && model.capabilities.output.hasCitations;
      required.output.hasWebSearch = required.output.hasWebSearch && model.capabilities.output.hasWebSearch;
      required.output.hasFunctionCalling = required.output.hasFunctionCalling && model.capabilities.output.hasFunctionCalling;
      required.output.hasToolUse = required.output.hasToolUse && model.capabilities.output.hasToolUse;
    }

    return required;
  };

  // Helper function to check if a model matches required capabilities
  const modelMatchesRequiredCapabilities = (model, requiredCapabilities) => {
    if (!requiredCapabilities) return true;

    // Check input capabilities
    if (requiredCapabilities.input.hasTextSupport && !model.capabilities.input.hasTextSupport) return false;
    if (requiredCapabilities.input.hasFileSupport && !model.capabilities.input.hasFileSupport) return false;
    if (requiredCapabilities.input.hasImageSupport && !model.capabilities.input.hasImageSupport) return false;
    if (requiredCapabilities.input.hasAudioSupport && !model.capabilities.input.hasAudioSupport) return false;
    if (requiredCapabilities.input.hasVideoSupport && !model.capabilities.input.hasVideoSupport) return false;

    // Check output capabilities
    if (requiredCapabilities.output.hasText && !model.capabilities.output.hasText) return false;
    if (requiredCapabilities.output.hasImages && !model.capabilities.output.hasImages) return false;
    if (requiredCapabilities.output.hasAudio && !model.capabilities.output.hasAudio) return false;
    if (requiredCapabilities.output.hasVideo && !model.capabilities.output.hasVideo) return false;
    if (requiredCapabilities.output.hasStreaming && !model.capabilities.output.hasStreaming) return false;
    if (requiredCapabilities.output.hasThinking && !model.capabilities.output.hasThinking) return false;
    if (requiredCapabilities.output.hasCitations && !model.capabilities.output.hasCitations) return false;
    if (requiredCapabilities.output.hasWebSearch && !model.capabilities.output.hasWebSearch) return false;
    if (requiredCapabilities.output.hasFunctionCalling && !model.capabilities.output.hasFunctionCalling) return false;
    if (requiredCapabilities.output.hasToolUse && !model.capabilities.output.hasToolUse) return false;

    return true;
  };

  // Get required capabilities from selected models
  const requiredCapabilities = useMemo(() => getRequiredCapabilitiesFromSelected(), [selectedModels]);

  // Helper function to check if model matches capability requirements from props
  const modelMatchesCapabilities = useCallback((model) => {
    // Check input capabilities
    if (inputs?.length > 0) {
      const hasAllInputs = inputs.every(input => {
        switch (input) {
          case 'text':
            return model.capabilities.input.hasTextSupport;
          case 'file':
            return model.capabilities.input.hasFileSupport;
          case 'image':
            return model.capabilities.input.hasImageSupport;
          case 'audio':
            return model.capabilities.input.hasAudioSupport;
          case 'video':
            return model.capabilities.input.hasVideoSupport;
          default:
            return false;
        }
      });
      if (!hasAllInputs) return false;
    }

    // Check output capabilities. Note the alias mapping:
    //   - 'image'    → output.hasImages
    //   - 'images'   → output.hasImages          (alias)
    //   - '3d-model' → output.has3D              (matches Tool.SUPPORTED_OUTPUT_TYPES)
    //   - '3d'       → output.has3D              (alias)
    //   - 'music'    → output.hasAudio           (no separate music capability)
    //   - 'code'     → output.hasText            (code is text)
    //   - 'document' → output.hasText            (documents are text)
    // If callers need finer-grained filtering (e.g. only image-to-3D models),
    // pass `capabilityPath` instead — see `Tool.modelFilter.capability`.
    if (outputs?.length > 0) {
      const out = model?.capabilities?.output || {};
      const hasAllOutputs = outputs.every(output => {
        switch (output) {
          case 'text':
          case 'code':
          case 'document':
            return !!out.hasText;
          case 'image':
          case 'images':
            return !!out.hasImages;
          case 'audio':
          case 'music':
            return !!out.hasAudio;
          case 'video':
            return !!out.hasVideo;
          case '3d-model':
          case '3d':
            return !!out.has3D;
          case 'streaming':   return !!out.hasStreaming;
          case 'thinking':    return !!out.hasThinking;
          case 'citations':   return !!out.hasCitations;
          case 'websearch':   return !!out.hasWebSearch;
          case 'functioncalling': return !!out.hasFunctionCalling;
          case 'tooluse':     return !!out.hasToolUse;
          default:
            return false;
        }
      });
      if (!hasAllOutputs) return false;
    }

    // Explicit dotted capability path (mirrors tool.modelFilter.capability).
    // Walks `model[a][b][c]` and only passes if the resolved value is truthy.
    if (capabilityPath) {
      const parts = String(capabilityPath).split('.');
      let cur = model;
      for (const p of parts) {
        if (cur == null) { cur = undefined; break; }
        cur = cur[p];
      }
      if (!cur) return false;
    }

    // Explicit slug allow-list (mirrors tool.modelFilter.slugs).
    if (allowedSlugs?.length > 0) {
      if (!model?.slug || !allowedSlugs.includes(model.slug)) return false;
    }

    // Check tags
    if (tags?.length > 0) {
      const hasAllTags = tags.every(tagId => 
        model.tags.some(modelTag => modelTag.id === tagId || modelTag.slug === tagId)
      );
      if (!hasAllTags) return false;
    }

    return true;
  }, [inputs, outputs, capabilityPath, allowedSlugs, tags]);

  // Transform model tags for Pills component
  const pillCategories = useMemo(() => {
    const allTags = Object.values(modelTags);
    return [
      { id: 'all', label: t('nav.modelSelectorDetailed.all') },
      ...allTags.map(tag => ({
        id: tag.slug,
        label: tag.name,
        icon: tag.icon
      }))
    ];
  }, [modelTags, t]);

  // Handle tag change
  const handleTagChange = useCallback((tagSlug) => {
    setActiveTag(tagSlug);
    if (tagSlug !== 'all') {
      fetchModelsByTag(tagSlug);
    }
  }, [fetchModelsByTag]);

  // Check if two models have the same type (input/output capabilities)
  const isSameModelType = useCallback((model1, model2) => {
    if (!model1 || !model2) return false;

    // Check if they share at least one common input interface
    const hasCommonInput = 
      (model1.capabilities.input.hasTextSupport && model2.capabilities.input.hasTextSupport) ||
      (model1.capabilities.input.hasImageSupport && model2.capabilities.input.hasImageSupport) ||
      (model1.capabilities.input.hasAudioSupport && model2.capabilities.input.hasAudioSupport) ||
      (model1.capabilities.input.hasVideoSupport && model2.capabilities.input.hasVideoSupport);

    // Check if they share at least one common output interface
    const hasCommonOutput = 
      (model1.capabilities.output.hasText && model2.capabilities.output.hasText) ||
      (model1.capabilities.output.hasImages && model2.capabilities.output.hasImages) ||
      (model1.capabilities.output.hasAudio && model2.capabilities.output.hasAudio) ||
      (model1.capabilities.output.hasVideo && model2.capabilities.output.hasVideo);

    return hasCommonInput && hasCommonOutput;
  }, []);

  // Filter out selected models and apply all filters
  const availableModels = useMemo(() => {
    let filteredModels = searchQuery.trim() 
      ? searchResults
      : activeTag === 'all'
        ? allModelIds.map(id => models[id]).filter(Boolean)
        : getModelsByTag(activeTag);
    
    // Apply capability filters if any capability/slug constraint is specified.
    // CRITICAL: every later branch (favorites, frequents, selected) re-applies
    // this same gate so wrong-modality models can't sneak back in. Previously
    // the "ensure favorites/frequents are included" branches added models
    // unconditionally, which meant a 3D tool would still show the user's
    // favorited image models in the picker (and worse, the legacy switch had
    // no '3d-model' case so the base filter dropped *everything* and the
    // bypassed favorites became the entire list).
    const hasCapabilityFilter = !!(inputs?.length || outputs?.length || capabilityPath || allowedSlugs?.length);
    if (hasCapabilityFilter) {
      filteredModels = filteredModels.filter(model => modelMatchesCapabilities(model));
    }

    // Ensure favorites are included in the list (for "All Models" tab)
    if (activeTag === 'all' && !searchQuery.trim()) {
      const seenIds = new Set(filteredModels.map(m => m.id));
      favoriteModelIds.forEach(favId => {
        const favModel = models[favId];
        if (!favModel || seenIds.has(favId)) return;
        if (hasCapabilityFilter && !modelMatchesCapabilities(favModel)) return;
        filteredModels = [...filteredModels, favModel];
        seenIds.add(favId);
      });
    }

    // Promote frequently-used models to the top (only for default "All" view, not search)
    if (activeTag === 'all' && !searchQuery.trim() && frequentModelIds.length > 0) {
      const seenFreqIds = new Set(filteredModels.map(m => m.id));
      frequentModelIds.forEach(fId => {
        const fModel = models[fId];
        if (!fModel || seenFreqIds.has(fId)) return;
        if (hasCapabilityFilter && !modelMatchesCapabilities(fModel)) return;
        filteredModels = [...filteredModels, fModel];
        seenFreqIds.add(fId);
      });

      const frequentSet = new Set(frequentModelIds);
      const frequentModels = [];
      const restModels = [];

      filteredModels.forEach(model => {
        if (frequentSet.has(model.id)) {
          frequentModels.push(model);
        } else {
          restModels.push(model);
        }
      });

      frequentModels.sort((a, b) => frequentModelIds.indexOf(a.id) - frequentModelIds.indexOf(b.id));

      filteredModels = [...frequentModels, ...restModels];
    }

    // Ensure currently selected models are included at the top, even if not in
    // the base list. Selected models also respect capability filters — if a
    // user navigates from a chat to a tool with stricter constraints, we'd
    // rather drop the carry-over selection than show an unusable model.
    const seenIds = new Set(filteredModels.map(m => m.id));
    selectedModels.forEach(sel => {
      if (!sel || seenIds.has(sel.id)) return;
      if (hasCapabilityFilter && !modelMatchesCapabilities(sel)) return;
      filteredModels = [sel, ...filteredModels];
      seenIds.add(sel.id);
    });

    return filteredModels;
  }, [
    searchQuery, 
    searchResults, 
    models, 
    allModelIds, 
    favoriteModelIds,
    frequentModelIds,
    activeTag, 
    getModelsByTag, 
    inputs, 
    outputs, 
    capabilityPath,
    allowedSlugs,
    modelMatchesCapabilities,
    selectedModels,
    selectedModelIds,
  ]);

  // Pagination for `All` is handled automatically by the model store
  // (it auto-fetches every page after the first call). We intentionally don't
  // trigger additional fetches on scroll — doing so raced with the auto-loop
  // and caused page 1 to be re-fetched indefinitely, preventing page 2+ from
  // ever landing.

  const handleToggleModel = useCallback((model) => {
    // Image-only OK for free users; video + other gated need Plus or paid credits
    const isVideoModel = !!model.capabilities?.output?.hasVideo;
    if (isModelLockedForUser(user, model)) {
      onRequestClose && onRequestClose();
      openModelAccessPaywall(openModal, {
        model,
        title: isVideoModel ? t('nav.modelSelectorDetailed.videoModelsPlus') : t('nav.modelSelectorDetailed.unlockAllModels'),
        subtitle: isVideoModel
          ? t('nav.modelSelectorDetailed.videoModelsDesc')
          : t('nav.modelSelectorDetailed.unlockAllDesc'),
      });
      return;
    }

    const isCurrentlySelected = selectedModelIds.includes(model.id);
    const selectionLimit = noMultiSelectMode || !isMultiSelectEnabledRef.current
      ? 1
      : maxModels;

    if (isCurrentlySelected) {
      if (noAutoMode && selectedModelIds.length === 1) {
        return;
      }

      const newSelectedIds = selectedModelIds.filter(id => id !== model.id);
      setSelectedModelIds(newSelectedIds);

      if (onChange) {
        onChange(newSelectedIds);
      }
    } else {
      if (selectedModelIds.length >= selectionLimit) {
        if (selectionLimit === 1) {
          const newSelectedIds = [model.id];
          setSelectedModelIds(newSelectedIds);

          if (onChange) {
            onChange(newSelectedIds);
          }
        }
      } else {
        const newSelectedIds = [...selectedModelIds, model.id];
        setSelectedModelIds(newSelectedIds);

        if (onChange) {
          onChange(newSelectedIds);
        }
      }
    }

    // Always dismiss in single-select mode. Multi-select (toggle on) keeps
    // the picker open until Done — callers historically passed
    // shouldCloseOnSelect=false for that flow, which also blocked dismiss
    // when the toggle was off and Done was hidden.
    if (selectionLimit === 1) {
      onRequestClose && onRequestClose();
    }
  }, [
    user,
    onRequestClose,
    openModal,
    t,
    selectedModelIds,
    noAutoMode,
    noMultiSelectMode,
    onChange,
    maxModels,
  ]);

  const handleShowModelInfo = useCallback((model) => setDetailsModelId(model.id), []);
  const handleHideModelInfo = useCallback(() => setDetailsModelId(null), []);
  const handleSelectFromInfo = useCallback(() => {
    const model = detailsModel;
    setDetailsModelId(null);
    if (model) handleToggleModel(model);
  }, [detailsModel, handleToggleModel]);

  const listModels = useMemo(() => {
    if (!showSelectedModels) return availableModels;
    return availableModels.filter(model => !selectedModelIdsSet.has(model.id));
  }, [availableModels, showSelectedModels, selectedModelIdsSet]);

  const primarySelectedModel = selectedModels[0] ?? null;

  const renderModelItem = useCallback(({ item: model }) => {
    const isSelected = selectedModelIdsSet.has(model.id);
    const isDisabled = restrictMultipleModelsToSameType &&
      isMultiSelectEnabled &&
      primarySelectedModel &&
      !isSelected &&
      !isSameModelType(primarySelectedModel, model);

    return (
      <ModelItem
        model={model}
        onActionClick={handleToggleModel}
        onInfoClick={handleShowModelInfo}
        isSelected={isSelected}
        isDisabled={isDisabled}
        userIsPlus={canUseGatedModels}
        colors={colors}
        isXS={isXS}
      />
    );
  }, [
    selectedModelIdsSet,
    restrictMultipleModelsToSameType,
    isMultiSelectEnabled,
    primarySelectedModel,
    isSameModelType,
    handleToggleModel,
    handleShowModelInfo,
    canUseGatedModels,
    colors,
    isXS,
  ]);

  const modelKeyExtractor = useCallback((model) => model.id, []);

  const handleAutoSelectPress = useCallback(() => {
    setSelectedModelIds([]);
    if (onChange) {
      onChange([]);
    }
    onRequestClose && onRequestClose();
  }, [onChange, onRequestClose]);

  const autoSelectHeader = useMemo(() => {
    if (noAutoMode || searchQuery.trim()) return null;
    return (
      <AutoSelectItem
        isSelected={selectedModelIds.length === 0}
        onPress={handleAutoSelectPress}
        colors={colors}
        isXS={isXS}
      />
    );
  }, [noAutoMode, searchQuery, selectedModelIds.length, handleAutoSelectPress, colors, isXS]);

  const listFooter = useMemo(() => {
    if (searchQuery.trim() || activeTag !== 'all' || isFetchedAllModels || !isLoadingAllModels) {
      return null;
    }
    return (
      <View style={styles.loadingContainer}>
        <Text color="secondary">{t('nav.modelSelectorDetailed.loadingMore')}</Text>
      </View>
    );
  }, [searchQuery, activeTag, isFetchedAllModels, isLoadingAllModels, styles.loadingContainer, t]);

  const isCatalogLoading = !searchQuery.trim() && activeTag === 'all' && !isFetchedAllModels && allModelIds.length === 0;

  // Newest catalog additions, surfaced as suggestions when a search comes up
  // empty. Gated by the same capability/slug/tag constraints as the main
  // list so a media-tool picker never suggests models it can't use.
  const latestModels = useMemo(() => {
    const pool = allModelIds.map(id => models[id]).filter(Boolean);
    const hasFilter = !!(inputs?.length || outputs?.length || capabilityPath || allowedSlugs?.length || tags?.length);
    const filtered = hasFilter ? pool.filter(m => modelMatchesCapabilities(m)) : pool;
    return [...filtered]
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
      .slice(0, 4);
  }, [allModelIds, models, inputs, outputs, capabilityPath, allowedSlugs, tags, modelMatchesCapabilities]);

  const listStatusContent = useMemo(() => {
    if (isSearching) {
      return (
        <View style={styles.loadingContainer}>
          <Text color="secondary">{t('nav.modelSelectorDetailed.searching')}</Text>
        </View>
      );
    }
    if (searchError) {
      return (
        <View style={styles.errorContainer}>
          <Text color="danger">{t('nav.modelSelectorDetailed.searchError')}</Text>
        </View>
      );
    }
    if (isCatalogLoading) {
      return (
        <View style={styles.loadingContainer}>
          <Text color="secondary">{t('nav.modelSelectorDetailed.loadingMore')}</Text>
        </View>
      );
    }
    if (listModels.length === 0 && searchQuery.trim()) {
      return (
        <SearchNoResults
          latestModels={latestModels}
          onActionClick={handleToggleModel}
          onInfoClick={handleShowModelInfo}
          userIsPlus={canUseGatedModels}
          colors={colors}
          isXS={isXS}
        />
      );
    }
    return null;
  }, [isSearching, searchError, isCatalogLoading, listModels.length, searchQuery, styles, t, latestModels, handleToggleModel, handleShowModelInfo, canUseGatedModels, colors, isXS]);

  const listExtraData = useMemo(
    () => `${selectedModelIds.join(',')}:${listModels.length}:${allModelIds.length}`,
    [selectedModelIds, listModels.length, allModelIds.length]
  );

  const handleMultiSelectToggle = (enabled) => {
    if (enabled && !userIsPlus) {
      // Non-Plus user trying to enable multi-select - show pricing modal
      openModal('PricingModal', {
        title: t('nav.modelSelectorDetailed.compareTitle'),
        subtitle: t('nav.modelSelectorDetailed.compareDesc')
      });
      return;
    }
    
    isMultiSelectEnabledRef.current = enabled;
    setIsMultiSelectEnabled(enabled);
    
    // If turning off multi-select and more than 1 model is selected, keep only the first
    if (!enabled && selectedModelIds.length > 1) {
      const newSelectedIds = [selectedModelIds[0]];
      setSelectedModelIds(newSelectedIds);
      if (onChange) {
        onChange(newSelectedIds);
      }
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    clearSearch();
  };

  const handleRemoveModel = (modelId) => {
    // Don't allow removing the last model if noAutoMode is true
    if (noAutoMode && selectedModelIds.length === 1) {
      return;
    }
    
    const newSelectedIds = selectedModelIds.filter(id => id !== modelId);
    setSelectedModelIds(newSelectedIds);
    
    if (onChange) {
      onChange(newSelectedIds);
    }
  };

  // Auto-select handler — used by the desktop V6-style view's "Auto" card
  // and parallels what the existing `AutoSelectItem` does inline below.
  // Kept as a hoisted handler so the desktop renderer doesn't need to
  // duplicate the close/onChange dance.
  const handleAutoSelect = () => {
    setSelectedModelIds([]);
    if (onChange) onChange([]);
    onRequestClose && onRequestClose();
  };

  // ----- Desktop (V6) data adapters --------------------------------------
  // The desktop view manages its own rail-based filtering, so we expose
  // two flat lists that are *only* gated by the parent-owned capability
  // / slug / tag filters — not by the legacy `activeTag` pill state. The
  // desktop component then narrows further with its rail (All, Frequent,
  // Favorites, per-tag) and renders the All view as collapsible sticky
  // sections grouped by primary tag.
  const hasCapabilityFilter = !!(inputs?.length || outputs?.length || capabilityPath || allowedSlugs?.length || tags?.length);
  const desktopFullCatalog = useMemo(() => {
    const list = allModelIds.map(id => models[id]).filter(Boolean);
    if (!hasCapabilityFilter) return list;
    return list.filter(m => modelMatchesCapabilities(m));
  }, [allModelIds, models, hasCapabilityFilter, modelMatchesCapabilities]);
  const desktopSearchCatalog = useMemo(() => {
    if (!hasCapabilityFilter) return searchResults || [];
    return (searchResults || []).filter(m => modelMatchesCapabilities(m));
  }, [searchResults, hasCapabilityFilter, modelMatchesCapabilities]);

  // Desktop / tablet uses the V6 rail-and-grid layout. Mobile (`isXS`)
  // keeps the original list-based UI below — its dense single-column
  // pattern is still the right call when the rail won't fit alongside.
  if (!isXS) {
    return (
      <View style={styles.container}>
        {detailsModel ? (
          <ModelInfoView
            model={detailsModel}
            isSelected={selectedModelIdsSet.has(detailsModel.id)}
            onBack={handleHideModelInfo}
            onSelect={handleSelectFromInfo}
            colors={colors}
            insets={insets}
          />
        ) : (
        <ModelSelectorDetailedDesktop
          searchCatalog={desktopSearchCatalog}
          fullCatalog={desktopFullCatalog}
          modelTags={modelTags}
          frequentModelIds={frequentModelIds}
          favoriteModelIds={favoriteModelIds}
          searchQuery={searchQuery}
          onSearchChange={handleSearch}
          onClearSearch={handleClearSearch}
          isSearching={isSearching}
          searchError={searchError}
          selectedModelIds={selectedModelIds}
          selectedModels={selectedModels}
          effectiveMaxModels={effectiveMaxModels}
          isMultiSelectEnabled={isMultiSelectEnabled}
          onToggleModel={handleToggleModel}
          onRemoveModel={handleRemoveModel}
          onMultiSelectToggle={handleMultiSelectToggle}
          onAuto={handleAutoSelect}
          onRequestClose={onRequestClose}
          onInfoClick={handleShowModelInfo}
          noAutoMode={noAutoMode}
          noMultiSelectMode={noMultiSelectMode}
          showDoneButton={showDoneButton}
          showSelectedModels={showSelectedModels}
          restrictMultipleModelsToSameType={restrictMultipleModelsToSameType}
          isSameModelType={isSameModelType}
          userIsPlus={userIsPlus}
          canUseGatedModels={canUseGatedModels}
          isLoadingAllModels={isLoadingAllModels}
          isFetchedAllModels={isFetchedAllModels}
          searchInputRef={searchInputRef}
          maxModels={maxModels}
        />
        )}
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.dropdownContentInner}>
        {detailsModel ? (
          <ModelInfoView
            model={detailsModel}
            isSelected={selectedModelIdsSet.has(detailsModel.id)}
            onBack={handleHideModelInfo}
            onSelect={handleSelectFromInfo}
            colors={colors}
            insets={insets}
          />
        ) : (
        <>
        <View style={styles.searchContainer}>
          <View style={styles.searchIconContainer}>
            <Icon 
              name="search" 
              size={14} 
              color={colors.text_color_secondary}
            />
          </View>
          <TextInput
            ref={searchInputRef}
            style={styles.searchInput}
            placeholder={t('nav.modelSelectorDetailed.search')}
            placeholderTextColor={colors.text_color_secondary}
            value={searchQuery}
            onChangeText={handleSearch}
          />
          {searchQuery.length > 0 && (
            <Pressable 
              style={styles.clearIconContainer}
              onPress={handleClearSearch}
            >
              <Icon 
                name="xmark"
                size={14} 
                color={colors.text_color_secondary}
              />
            </Pressable>
          )}
        </View>

        {/* Only show multi-select toggle and pills container when not searching and no inputs/outputs specified */}
        {isTopHidden && (
          <>
            {!noMultiSelectMode && (
              <View style={styles.autoContainer}>
                <Text color="secondary" size="xs">{t('nav.modelSelectorDetailed.multiSelect')}</Text>
                <Text color="tertiary" size="xs" style={styles.autoContainerDescription}>· {t('nav.modelSelectorDetailed.compareModels')}</Text>
                {!userIsPlus && (
                  <View style={styles.plusOnlyContainer}>
                    <Icon name="sparkles" solid size={8} color={PLUS_COLORS.iconColor} style={styles.plusOnlyIcon} />
                    <Text color={PLUS_COLORS.textColor} size="xxs" weight="extraBold" style={styles.plusOnlyText}>PLUS</Text>
                  </View>
                )}
                <Switch
                  value={isMultiSelectEnabled}
                  onValueChange={handleMultiSelectToggle}
                />
              </View>
            )}

            <View style={styles.pillsOuterContainer}>
              <Pills
                hideArrows={true}
                items={pillCategories}
                activeId={activeTag}
                onChange={handleTagChange}
                size='xs'
                isLoading={isLoadingModelTags}
                containerStyle={styles.pillsContainer}
                hasBackground={false}
                isTransparent={true}
              />
            </View>
          </>
        )}

        {useVirtualizedList ? (
          listStatusContent ? (
            <View style={styles.scrollableContainer}>
              {!noAutoMode && !searchQuery.trim() && autoSelectHeader}
              {listStatusContent}
            </View>
          ) : (
            <View style={styles.scrollableContainer}>
              <FlashList
                data={listModels}
                renderItem={renderModelItem}
                keyExtractor={modelKeyExtractor}
                estimatedItemSize={48}
                extraData={listExtraData}
                style={{ flex: 1 }}
                contentContainerStyle={styles.scrollableContainerContent}
                showsVerticalScrollIndicator={true}
                keyboardShouldPersistTaps="handled"
                ListHeaderComponent={autoSelectHeader}
                ListFooterComponent={listFooter}
                drawDistance={250}
              />
            </View>
          )
        ) : (
          <ScrollView
            style={[styles.scrollableContainer, Platform.OS === 'web' && styles.scrollableContainerWeb]}
            contentContainerStyle={styles.scrollableContainerContent}
            showsVerticalScrollIndicator={true}
            keyboardShouldPersistTaps="handled"
            bounces={true}
            nestedScrollEnabled={true}
            scrollEnabled={true}
          >
            {listStatusContent ?? (
              <>
                {autoSelectHeader}
                {listModels.map((model) => {
                  const isSelected = selectedModelIdsSet.has(model.id);
                  const isDisabled = restrictMultipleModelsToSameType &&
                    isMultiSelectEnabled &&
                    primarySelectedModel &&
                    !isSelected &&
                    !isSameModelType(primarySelectedModel, model);

                  return (
                    <ModelItem
                      key={`model-${model.id}`}
                      model={model}
                      onActionClick={handleToggleModel}
                      onInfoClick={handleShowModelInfo}
                      isSelected={isSelected}
                      isDisabled={isDisabled}
                      userIsPlus={canUseGatedModels}
                      colors={colors}
                      isXS={isXS}
                    />
                  );
                })}
              </>
            )}
            {listFooter}
          </ScrollView>
        )}

        {showSelectedModels && selectedModels.length > 0 && (
          <View 
            style={styles.selectedModelsContainer}
          >
            <View style={styles.selectedModelsTitle}>
              <Text color="secondary" size="xs">{t('nav.modelSelectorDetailed.selectedModels')} ({selectedModels.length})</Text>
              <Text color="tertiary" size="xs">{t('nav.modelSelectorDetailed.maxLabel')} {effectiveMaxModels}</Text>
            </View>
            <View style={styles.selectedModelsList}>
              {selectedModels.map((model, i) => (
                <Pressable
                  key={`selected-model-${model.id}-${i}`}
                  style={styles.selectedModelChip}
                  onPress={() => handleRemoveModel(model.id)}
                >
                  <Text size="n" color="primary">{model.name}</Text>
                  <Icon name="xmark" size={12} color={colors.text_color_secondary} />
                </Pressable>
              ))}
            </View>
          </View>
        )}

        {showDoneButton && effectiveMaxModels > 1 && (
          <View style={styles.doneButtonContainer}>
            <Button
              text={t('common.done')}
              variant="primary"
              size="medium"
              onPress={onRequestClose}
              isFullWidth
            />
          </View>
        )}
        </>
        )}
      </View>
    </View>
  );
};

ModelSelectorDetailed.propTypes = {
  modelIds: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ]),
  onChange: PropTypes.func,
  maxModels: PropTypes.number,
  inputs: PropTypes.arrayOf(PropTypes.string),
  outputs: PropTypes.arrayOf(PropTypes.string),
  tags: PropTypes.arrayOf(PropTypes.string),
  shouldCloseOnSelect: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onRequestClose: PropTypes.func,
  noAutoMode: PropTypes.bool,
  noMultiSelectMode: PropTypes.bool,
  showDoneButton: PropTypes.bool,
  showSelectedModels: PropTypes.bool,
  restrictMultipleModelsToSameType: PropTypes.bool,
  useBottomSheetScroll: PropTypes.bool,
};

export default ModelSelectorDetailed; 
